import { useMemo } from 'react'

import envConfig from '~/configs/env'
import { useCurrentUser, useLanguage } from '~/data/api/hooks/users'

const SUPPORTED_FRENCH_REGIONS = ['BE', 'CA', 'CH', 'FR']
const SUPPORTED_GERMAN_REGIONS = ['BE', 'CH', 'DE', 'IT']

/**
 * Combines user language and locale preferences to produce a BCP 47-compliant language tag
 * that uses the user's language as well as their locale (if possible), otherwise just the language.
 *
 * The fundamental problem this hook solves is that if we just use the user's locale to format
 * date strings, we may end up rendering month/day names in a language specific to the locale
 * but which differs from their language preference. For example, a user who speaks French
 * but lives in Germany (locale: de-DE) would see German month names if we only used their
 * locale for date formatting. Because fr-DE is not a valid BCP 47 language tag, we fall back
 * to just the language code.
 *
 * This hook intelligently combines the user's preferred language with their locale's region
 * when that combination represents a valid BCP 47 language tag (e.g., en-DE for English
 * language with German region). If the combination is not linguistically valid (e.g.,
 * French with German region), it falls back to just the language code.
 *
 * @returns {string} A BCP 47 language tag (e.g., "en-US", "fr-CA") or language code (e.g., "en")
 *
 * @example
 * // User with preferredLanguage: "en", locale: "de-DE"
 * // Returns: "en-DE" (English language with German region)
 *
 * @example
 * // User with preferredLanguage: "fr", locale: "de-DE"
 * // Returns: "fr" (French language only, as fr-DE is not valid)
 */
export default function useDateTimeLocale() {
  const user = useCurrentUser().synopUser
  const language = useLanguage()
  const locale = user?.locale ?? envConfig.defaultLocale

  // If the language and locale can be combined into a valid BCP 47 language tag, return that
  return useMemo(() => {
    const region = locale.split('-')[1] ?? 'US'

    // English works for all supported regions
    if (language === 'en') return `${language}-${region}`
    // French works in Belgium, Canada, Switzerland and France
    else if (language === 'fr') {
      if (SUPPORTED_FRENCH_REGIONS.includes(region)) return `${language}-${region}`
    }

    // German works in Belgium, Switzerland, Germany and Italy
    else if (language === 'de') {
      if (SUPPORTED_GERMAN_REGIONS.includes(region)) return `${language}-${region}`
    }

    // Otherwise
    return language
  }, [locale, language])
}
